import { useCallback, useEffect, useRef } from "react";
import { useInterval } from "react-use";
import type { useTracking } from "./TrackingProvider.tsx";

const PING_INTERVAL = 60 * 1000;

export function useActivityTracking(track: ReturnType<typeof useTracking>) {
  const isUserActiveWithin = useIsUserActive();

  useEffect(() => {
    const handleLinkClick = (event: MouseEvent) => {
      if (!(event.target instanceof Element)) {
        return;
      }

      // If a cancel button was clicked, we don't want to track the link
      // click, since it was likely a cancel action and not intended to open the
      // link of the containing anchor element.
      if (event.defaultPrevented) {
        const target = event.target;

        if (
          target.closest("[data-track-link-name='Cancel']") ||
          target.closest("[data-track-link-name='Close']")
        ) {
          return;
        }
      }

      const link = event.target.closest("a");

      if (!link) {
        return;
      }

      const href = link.getAttribute("href");

      if (!href) {
        return;
      }

      const text = link.innerText || link.getAttribute("aria-label") || "";
      const name = link.dataset.trackLinkName || text;

      const isSameDomain = href.startsWith(window.location.origin);
      const isWithoutProtocol = href.startsWith("/") && !href.startsWith("//");

      // Also handle special case of MLB links, where the app is hosted on the
      // mlb.com domain, but we want to track other mlb.com links as external.
      const isMLBLink = href.includes("mlb.com");

      if ((isSameDomain && !isMLBLink) || isWithoutProtocol) {
        track("LINK_CLICK", {
          href: href,
          text: text,
          name: name,
          ...getEventContext(link),
        });
      } else {
        track("EXTERNAL_LINK_CLICK", {
          href: href,
          text: text,
          name: name,
          ...getEventContext(link),
        });
      }
    };

    document.addEventListener("click", handleLinkClick);

    return () => {
      document.removeEventListener("click", handleLinkClick);
    };
  }, [track]);

  useEffect(() => {
    const handleButtonClick = (event: MouseEvent) => {
      if (!(event.target instanceof Element)) {
        return;
      }

      const button = event.target.closest("button");

      if (!button) {
        return;
      }

      const text = button.innerText || button.getAttribute("aria-label") || "";
      const name = button.dataset.trackLinkName || text;

      track("BUTTON_CLICK", {
        text: text,
        name,
        ...getEventContext(button),
      });
    };

    document.addEventListener("click", handleButtonClick);

    return () => {
      document.removeEventListener("click", handleButtonClick);
    };
  }, [track]);

  useInterval(() => {
    // If the user is active, ping the server to keep the session alive.
    if (isUserActiveWithin(PING_INTERVAL)) {
      track("PING");
    }
  }, PING_INTERVAL);
}

function useIsUserActive() {
  // A user is active if they have taken any of the following actions within the
  // threshold (in milliseconds).
  //
  // - Clicked anything
  // - Typed anything
  // - Moved the mouse or cursor

  const lastActionTimestampRef = useRef<number>(Date.now());

  useEffect(() => {
    const handleAction = () => {
      lastActionTimestampRef.current = Date.now();
    };

    document.addEventListener("click", handleAction);
    document.addEventListener("keydown", handleAction);
    document.addEventListener("mousemove", handleAction);

    return () => {
      document.removeEventListener("click", handleAction);
      document.removeEventListener("keydown", handleAction);
      document.removeEventListener("mousemove", handleAction);
    };
  }, []);

  return useCallback(
    (thresholdMs: number) =>
      Date.now() - lastActionTimestampRef.current < thresholdMs,
    [],
  );
}

/**
 * Returns a function that will recursively search for the given property on
 * an element and its parents.
 */
function getPropertyRecursive(property: string) {
  return (element: Element | null): string | null => {
    const propertyValue = element?.getAttribute(property);

    if (propertyValue) {
      return propertyValue;
    }

    if (element?.parentElement) {
      return getPropertyRecursive(property)(element.parentElement);
    }

    return null;
  };
}

const getPageName = getPropertyRecursive("data-page-name");
const getSectionName = getPropertyRecursive("data-section-name");
const getDialogName = getPropertyRecursive("data-dialog-name");

function getEventContext(element: Element | null) {
  const pageName = getPageName(element);
  const sectionName = getSectionName(element);
  const dialogName = getDialogName(element);

  return {
    // TODO: This is blank when the button is in a dialog or something else
    // rendered by a portal.
    ...(pageName ? { page: pageName } : {}),
    ...(sectionName ? { section: sectionName } : {}),
    ...(dialogName ? { dialog: dialogName } : {}),
  };
}
